/**
 * AuthContext — Customer App
 * Source of truth: Spring Boot + MongoDB.
 * localStorage: JWT tokens + user profile only.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  loginUser, registerUser, logoutUser, isAuthenticated,
  createBookingApi, cancelBookingApi, rescheduleBookingApi,
  getMyBookings, getMyActiveBooking, clearTokens,
} from '../api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user,          setUser]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [authModal,     setAuthModal]     = useState(null);
  const [authRedirect,  setAuthRedirect]  = useState(null);
  const [userBookings,  setUserBookings]  = useState([]);
  const [activeBooking, setActiveBooking] = useState(null);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [tokenValid,    setTokenValid]    = useState(false);

  /* ── Boot: restore session ── */
  useEffect(() => {
    const tryRestore = async () => {
      const stored = localStorage.getItem('ae_user');
      const hasToken = isAuthenticated();
      if (stored && hasToken) {
        try {
          const parsed = JSON.parse(stored);
          setUser(parsed);
          setTokenValid(true);
        } catch { clearTokens(); }
      } else {
        // Clear stale user data if no valid token
        if (stored) localStorage.removeItem('ae_user');
      }
      setLoading(false);
    };
    tryRestore();
  }, []);

  /* ── Fetch bookings from DB when user is authenticated ── */
  const refreshBookings = useCallback(async () => {
    if (!isAuthenticated()) return;
    setBookingsLoading(true);
    try {
      const [all, active] = await Promise.all([
        getMyBookings(),
        getMyActiveBooking().catch(() => null),
      ]);
      setUserBookings(all || []);
      setActiveBooking(active || null);
      setTokenValid(true);
    } catch (e) {
      const status = e.response?.status;
      if (status === 401 || status === 403) {
        // Token is invalid/expired — log the user out
        setTokenValid(false);
        clearTokens();
        setUser(null);
        setUserBookings([]);
        setActiveBooking(null);
      }
      console.warn('Bookings fetch error:', e.message);
    } finally {
      setBookingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && tokenValid) refreshBookings();
  }, [user, tokenValid, refreshBookings]);

  /* ── Poll active booking every 5s ONLY when token is valid ── */
  useEffect(() => {
    if (!user || !tokenValid) return;
    const iv = setInterval(async () => {
      try {
        const active = await getMyActiveBooking();
        setActiveBooking(active || null);
      } catch (e) {
        const status = e.response?.status;
        if (status === 401 || status === 403) {
          setTokenValid(false);
          clearTokens();
          setUser(null);
          setActiveBooking(null);
        }
      }
    }, 10000); // every 10s (reduced from 5s to cut log noise)
    return () => clearInterval(iv);
  }, [user, tokenValid]);

  /* ── Auth actions ── */
  const login = async (email, password) => {
    try {
      const result = await loginUser({ email, password });
      if (!result?.user) return { success: false, error: 'Login failed. Please try again.' };
      setUser(result.user);
      setTokenValid(true);
      localStorage.setItem('ae_user', JSON.stringify(result.user));
      return { success: true };
    } catch (e) {
      const status = e.response?.status;
      if (status === 401 || status === 400) {
        return { success: false, error: 'Incorrect email or password. Please try again.' };
      }
      return { success: false, error: e.response?.data?.message || 'Login failed. Please check your connection.' };
    }
  };

  const signup = async ({ name, email, phone, password, vehicle }) => {
    try {
      const result = await registerUser({ name, email, phone, password, vehicle });
      if (!result?.user) return { success: false, error: 'Registration failed. Please try again.' };
      setUser(result.user);
      setTokenValid(true);
      localStorage.setItem('ae_user', JSON.stringify(result.user));
      return { success: true };
    } catch (e) {
      const msg = e.response?.data?.message || '';
      // Surface specific DB errors from backend
      if (msg.toLowerCase().includes('email'))
        return { success: false, error: 'This email address is already registered. Please sign in.' };
      if (msg.toLowerCase().includes('phone'))
        return { success: false, error: 'This phone number is already registered.' };
      if (e.response?.status === 400)
        return { success: false, error: msg || 'Please check your details and try again.' };
      return { success: false, error: 'Registration failed. Please check your connection.' };
    }
  };

  const logout = () => {
    logoutUser();
    setUser(null);
    setTokenValid(false);
    setUserBookings([]);
    setActiveBooking(null);
    localStorage.removeItem('ae_user');
  };

  /* ── Booking actions (all go to DB) ── */
  const addBooking = useCallback(async formData => {
    if (!user) return null;
    const booking = await createBookingApi({
      vehicleMake:     formData.vehicleMake,
      vehicleModel:    formData.vehicleModel,
      vehicleYear:     formData.vehicleYear  || '',
      regNumber:       formData.regNumber,
      fuelType:        formData.fuelType,
      services:        formData.services,
      additionalNotes: formData.additionalNotes || formData.notes || '',
      date:            formData.date,
      timeSlot:        formData.timeSlot,
      pickupType:
        formData.pickupType === 'pickup'  ? 'VEHICLE_PICKUP'  :
        formData.pickupType === 'dropoff' ? 'CUSTOMER_DROPOFF' :
        formData.pickupType === 'self'    ? 'SELF_DRIVE_IN'   :
        formData.pickupType,                // pass through enum directly if already normalised
      address:  formData.address  || '',
      landmark: formData.landmark || '',
    });
    // Refresh from DB immediately
    await refreshBookings();
    return booking;
  }, [user, refreshBookings]);

  const cancelBooking = useCallback(async bookingId => {
    await cancelBookingApi(bookingId);
    await refreshBookings();
  }, [refreshBookings]);

  const updateBookingTime = useCallback(async (bookingId, date, timeSlot) => {
    await rescheduleBookingApi(bookingId, date, timeSlot);
    await refreshBookings();
  }, [refreshBookings]);

  /* ── Modal helpers ── */
  const requireAuth = (path = null) => { setAuthRedirect(path); setAuthModal('login'); };
  const openLogin   = ()            => setAuthModal('login');
  const openSignup  = ()            => setAuthModal('signup');
  const closeAuth   = ()            => { setAuthModal(null); setAuthRedirect(null); };

  return (
    <AuthContext.Provider value={{
      user, loading, authModal, authRedirect, tokenValid,
      login, signup, logout,
      requireAuth, openLogin, openSignup, closeAuth,
      userBookings, activeBooking, bookingsLoading,
      addBooking, cancelBooking, updateBookingTime,
      refreshBookings,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);